import { useState } from "react";
import { LinkIcon } from "lucide-react";
import ScopeViewRendererCard from "@/components/ui/others/ModuleScopeRenderers/ScopeViewRendererCard";
import { getNicheEntry, type ModuleConnector } from "./nicheRegistry";
import { useRequiredFieldMark } from "@/features/leads/requirements/requiredFieldMarks";
import { RequiredStar } from "@/features/leads/requirements/requiredFieldMarks.ui";
import { usePermissions } from "@/rbac/usePermissions";
import {
  DEAL_EDIT_DENIED_MESSAGE,
  dealStageIdOf,
  useCanEditDeal,
} from "@/rbac/useCanEditDeal";

/** Shown on the create-mode slot when the role may not add a deal in this stage. */
const DEAL_CREATE_DENIED_MESSAGE = "You can't add deals in this stage";

type Props = {
  /** The deal the slot describes — null in CREATE mode (see `lead`). */
  child: any;
  /**
   * CREATE mode: the lead has no deal yet and sits in a Choices stage, so the
   * slot offers to attach an item AND create the lead's first deal with it.
   */
  lead?: any;
  connector: ModuleConnector;
  /** CREATE mode: the stage the first deal will be created in (the lead's own when it holds deals, else the first Choices stage). */
  createStageId?: number | null;
  /** CREATE mode, lead before the first Choices stage: that stage's name, for the hint. */
  createStageName?: string | null;
};

/**
 * Pull the entries from `child.modules` that belong to this connector.
 *
 * IMPORTANT: per the payment-and-deal-system rule, the key inside `child.modules.<TYPE>`
 * is derived from `connectionLabel` (lowercased/normalised), NOT from `nicheModelName`.
 * So we always need a fallback that scans entries by their `model` field.
 */
function getAttachedEntries(child: any, connector: ModuleConnector): any[] {
  const bucket = child?.modules?.[connector.type];
  if (!bucket) return [];

  if (connector.type === "ONE_TO_ONE") {
    const direct = bucket[connector.nicheModelName];
    if (direct) return [direct];
    for (const key of Object.keys(bucket)) {
      const entry = bucket[key];
      if (entry && typeof entry === "object" && entry.model === connector.nicheModelName) {
        return [entry];
      }
    }
    return [];
  }

  // ONE_TO_MANY / MANY_TO_MANY → arrays
  const direct = bucket[connector.nicheModelName];
  if (Array.isArray(direct) && direct.length > 0) return direct;

  for (const key of Object.keys(bucket)) {
    const value = bucket[key];
    if (Array.isArray(value) && value.length > 0) {
      if (key === connector.nicheModelName || value[0]?.model === connector.nicheModelName) {
        return value;
      }
    }
  }
  return [];
}

export default function NicheAttachmentSlot({ child, lead, connector, createStageId, createStageName }: Props) {
  const [open, setOpen] = useState(false);
  // Stage-requirement star — `Modules` requirements key on the connector id.
  const required = useRequiredFieldMark("module", connector.id);
  const canEditDeal = useCanEditDeal();
  const { canStage, bypassesRbacChecks } = usePermissions();
  const entry = getNicheEntry(connector.nicheModelName);
  const createMode = !child && !!lead?.id;
  const attachedEntries = createMode ? [] : getAttachedEntries(child, connector);
  const isAttached = attachedEntries.length > 0;

  // Unregistered niche → preserve legacy behaviour: show generic card if attached, nothing if not.
  // This guarantees no regression for industries (e.g. Travel) that previously rendered via ScopeProfileView.
  if (!entry) {
    if (!isAttached) return null;
    const title = (
      attachedEntries[0]?.label ||
      connector.connectionLabel ||
      connector.nicheModelName
    ).replace(/Varients/gi, "Variants");
    return <ScopeViewRendererCard title={title} items={attachedEntries} />;
  }

  if (isAttached) {
    const View = entry.AttachedView;
    return <View child={child} connector={connector} />;
  }

  // Attaching to an existing deal edits its niche links ("Choices → Edit
  // choices" on the deal's own stage). In CREATE mode the attachment ADDS a
  // deal in the lead's stage ("Choices → Create"). A role without it still
  // sees the slot — and the stage-requirement star, which is information —
  // but inert.
  // The deal is created in `createStageId` (the lead's own stage, or the first
  // Choices stage when the lead sits before it) — that is the stage whose
  // "Choices → Create" grant applies.
  const leadStageId = Number(createStageId ?? lead?.stageId ?? lead?.stage?.id);
  const canAttach = createMode
    ? bypassesRbacChecks ||
      (Number.isFinite(leadStageId) &&
        canStage(leadStageId, "choice_management", "create"))
    : canEditDeal(dealStageIdOf(child));
  const deniedMessage = createMode
    ? DEAL_CREATE_DENIED_MESSAGE
    : DEAL_EDIT_DENIED_MESSAGE;

  const Modal = entry.AttachModal;
  const label = entry.attachLabel || connector.connectionLabel || "Module";

  return (
    <>
      <button
        type="button"
        onClick={canAttach ? () => setOpen(true) : undefined}
        disabled={!canAttach}
        title={canAttach ? undefined : deniedMessage}
        className="w-full px-3 py-2.5 rounded-[12px] border border-dashed border-[#c5c9c7] bg-[#fafbfa] hover:bg-[#f0f2f1] flex items-center justify-center gap-1.5 text-[13px] text-[#697571] transition-colors disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:bg-[#fafbfa]"
      >
        <LinkIcon className="size-3.5" />
        <span>
          Attach <span className="font-medium text-[#1e302a]">{label}</span>
          <RequiredStar mark={required} />
          {createMode && (
            <span className="text-[12px] text-[#9ba3a0]">
              {" "}
              · creates this lead's first deal{createStageName ? ` in ${createStageName}` : ""}
            </span>
          )}
        </span>
      </button>

      {canAttach && (
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          child={child}
          connector={connector}
          createForLead={
            createMode ? { id: Number(lead.id), name: lead?.name } : null
          }
        />
      )}
    </>
  );
}
